// Customer follow-up list PDF export. Portrait, letter size.
// Prints exactly what the user is looking at: the current tab, the current
// search, and the current page of results at the current page size.
// Styled to match the Bonus Management schedule PDF.

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

import Utils from '../Utils/Utils';

const INK = [11, 19, 84];
const INDIGO_SOFT = [238, 240, 254];
const MUTED = [122, 128, 155];
const AMBER = [255, 201, 60];
const LINE = [225, 228, 242];
const TEXT = [28, 32, 52];

function fullName(customer) {
  const first = (customer?.firstName || '').trim();
  const last = (customer?.lastName || '').trim();
  const name = `${first} ${last}`.trim();
  return name || '(no name)';
}

// (123) 456-7890 for 10-digit numbers, otherwise leave the raw value alone.
function formatPhone(phone) {
  if (!phone) return '';
  const digits = phone.toString().replace(/\D/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits.startsWith('1')) {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return phone.toString();
}

// lastVisit comes through either as "MM/DD/YYYY hh:mm AM" or as an ISO-ish
// "YYYY-MM-DD ..." string. Normalize the ISO form to MM/DD/YYYY so the column
// reads the same way the rest of the app renders dates.
function formatLastVisit(lastVisit) {
  if (!lastVisit) return '';
  const value = lastVisit.toString().trim();
  const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})(.*)/);
  if (isoMatch) {
    const [, year, month, day, rest] = isoMatch;
    return `${month}/${day}/${year}${rest}`;
  }
  return value;
}

/**
 * @param customers  the rows currently on screen (already tab-filtered,
 *                   searched, sorted and paginated by TablePage)
 * @param options    { locationName, tabLabel, search, page, rowsPerPage, totalRows, paginated }
 */
export function downloadCustomerFollowUpPdf(customers, options = {}) {
  const {
    locationName,
    tabLabel = 'Customers',
    search = '',
    page = 0,
    rowsPerPage = 0,
    totalRows = 0,
    paginated = true,
  } = options;

  const rows = customers || [];

  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'pt',
    format: 'letter',
  });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 44;
  const contentW = pageWidth - margin * 2;

  const stamp = new Date().toLocaleString('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });

  // Draw a letter-spaced label without leaking char spacing into later text.
  const spacedLabel = (text, x, yy, space) => {
    doc.setCharSpace(space);
    doc.text(text, x, yy);
    doc.setCharSpace(0);
  };

  // ── Header band (page 1) ──────────────────────────────────────────────────
  doc.setFillColor(...INK);
  doc.rect(0, 0, pageWidth, 92, 'F');
  doc.setFillColor(...AMBER);
  doc.rect(0, 92, pageWidth, 3, 'F');

  doc.setTextColor(...INDIGO_SOFT);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  spacedLabel('CUSTOMER FOLLOW-UP LIST', margin, 34, 2);

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(20);
  doc.text(locationName || 'Location', margin, 60);

  doc.setTextColor(200, 205, 230);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text(`Generated ${stamp}`, margin, 78);

  // Right side of the band: which tab, and which slice of results this is.
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(255, 255, 255);
  doc.text(`${tabLabel.toUpperCase()}`, pageWidth - margin, 46, {
    align: 'right',
  });

  const totalPages =
    paginated && rowsPerPage > 0
      ? Math.max(1, Math.ceil(totalRows / rowsPerPage))
      : 1;
  const rangeStart = rows.length === 0 ? 0 : page * rowsPerPage + 1;
  const rangeEnd = page * rowsPerPage + rows.length;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(200, 205, 230);
  const scopeLine = paginated
    ? `Page ${
        page + 1
      } of ${totalPages}  ·  showing ${rangeStart}-${rangeEnd} of ${totalRows}`
    : `${rows.length} customer${rows.length === 1 ? '' : 's'}`;
  doc.text(scopeLine, pageWidth - margin, 64, { align: 'right' });

  if (search) {
    doc.text(`Search: "${search}"`, pageWidth - margin, 78, { align: 'right' });
  }

  let y = 124;

  // ── Table ─────────────────────────────────────────────────────────────────
  if (rows.length === 0) {
    doc.setDrawColor(...LINE);
    doc.setLineWidth(0.6);
    doc.line(margin, y, pageWidth - margin, y);
    y += 26;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(...MUTED);
    doc.text('No customers on this page.', margin, y);
  } else {
    const body = rows.map((c, i) => [
      `${page * rowsPerPage + i + 1}`,
      fullName(c),
      formatPhone(c?.phone) || '—',
      formatLastVisit(c?.lastVisit) || '—',
    ]);

    autoTable(doc, {
      startY: y,
      head: [['#', 'CUSTOMER NAME', 'PHONE NUMBER', 'LAST VISIT']],
      body,
      theme: 'striped',
      margin: { left: margin, right: margin, top: margin, bottom: 56 },
      tableWidth: contentW,
      headStyles: {
        fillColor: INDIGO_SOFT,
        textColor: INK,
        fontStyle: 'bold',
        fontSize: 7.5,
        cellPadding: { top: 6, bottom: 6, left: 8, right: 8 },
      },
      bodyStyles: { fontSize: 10, textColor: TEXT, cellPadding: 7 },
      alternateRowStyles: { fillColor: [249, 250, 253] },
      columnStyles: {
        0: { cellWidth: 30, textColor: MUTED, fontSize: 9 },
        1: { fontStyle: 'bold', textColor: INK },
        2: { cellWidth: 128, halign: 'right', fontStyle: 'bold' },
        3: { cellWidth: 122, halign: 'right', fontSize: 9, textColor: MUTED },
      },
      didParseCell: (data) => {
        if (data.section === 'head' && data.column.index >= 2) {
          data.cell.styles.halign = 'right';
        }
        // Missing values read as muted rather than as a real value.
        if (
          data.section === 'body' &&
          data.column.index === 2 &&
          data.cell.raw === '—'
        ) {
          data.cell.styles.textColor = MUTED;
          data.cell.styles.fontStyle = 'normal';
        }
      },
    });
  }

  // ── Footer on every page ──────────────────────────────────────────────────
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i += 1) {
    doc.setPage(i);
    doc.setDrawColor(...LINE);
    doc.setLineWidth(0.6);
    doc.line(margin, pageHeight - 34, pageWidth - margin, pageHeight - 34);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    doc.text('Generated by NuVe System', margin, pageHeight - 20);
    doc.text(`Page ${i} of ${pageCount}`, pageWidth - margin, pageHeight - 20, {
      align: 'right',
    });
  }

  const safe = (text, fallback) =>
    (text || fallback).replace(/[^a-z0-9]+/gi, '-').toLowerCase();

  const fileName = paginated
    ? `customer-followup-${safe(locationName, 'location')}-${safe(
        tabLabel,
        'list'
      )}-page-${page + 1}.pdf`
    : `customer-followup-${safe(locationName, 'location')}-${safe(
        tabLabel,
        'list'
      )}.pdf`;

  // Route through the shared bridge (same as the Bonus schedule PDF): on the
  // mobile/tablet app this opens the native PDF viewer with print + share;
  // on the web it downloads the file.
  const pdfData = doc.output('arraybuffer');
  Utils.handlePdfAction(pdfData, 'download', fileName);
}
